<template>
  <div class="confirm-step">
    <!-- tabindex="-1" lets the mount hook focus the summary, announcing the step change
         to assistive technology. -->
    <p ref="summary" class="confirm-step__summary" tabindex="-1">{{ summaryText }}</p>

    <!-- Above the table: a confirm failure must be visible without scrolling past
         a long preview. -->
    <div class="confirm-step__error" role="alert">
      {{ store.errorMessage }}
    </div>

    <ul v-if="fileErrors.length > 0" class="confirm-step__warning" tabindex="0" :aria-label="store.labels.fileErrors">
      <li v-for="(error, index) in fileErrors" :key="index">{{ error }}</li>
    </ul>

    <PreviewTable
      :rows="previewRows"
      :selected-keys="store.selectedRowKeys"
      :all-selected="store.allRowsSelected"
      :indeterminate="store.selectionIndeterminate"
      :outcome-labels="store.outcomeLabels"
      :status-labels="store.statusLabels"
      :labels="store.labels"
      :client-control-enabled="store.clientControlEnabled"
      @toggle-row="store.toggleRow"
      @toggle-all="store.toggleAllRows"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, useTemplateRef } from "vue";
import PreviewTable from "./PreviewTable.ce.vue";
import { useAssignSpecialtiesStore } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/store/assign-specialties";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

const store = useAssignSpecialtiesStore();
const summaryElement = useTemplateRef("summary");

function focusSummary() {
  summaryElement.value?.focus();
}

onMounted(focusSummary);

const summary = computed(() => ({
  new: 0,
  existing: 0,
  will_enable: 0,
  errors: 0,
  total: 0,
  ...(store.previewData?.summary ?? {}),
}));
const previewRows = computed(() => store.previewData?.rows ?? []);
const fileErrors = computed(() => store.previewData?.errors ?? []);

// Compose the summary sentence from server-provided templates so the wording
// localizes and adapts per deployment (e.g. non-canonical "will be added" vs
// canonical "will be requested"). The will-enable fragment only applies on
// deployments with client control; the errors fragment only when errors exist.
const summaryText = computed(() => {
  const templates = store.labels.summary;
  const parts = [
    pluralizeTemplate(templates.new, summary.value.new),
    pluralizeTemplate(templates.existing, summary.value.existing),
  ];

  if (store.clientControlEnabled && summary.value.will_enable > 0) {
    parts.push(pluralizeTemplate(templates.willEnable, summary.value.will_enable));
  }

  if (summary.value.errors > 0) {
    parts.push(pluralizeTemplate(templates.errors, summary.value.errors));
  }

  return parts.join(" ");
});
</script>

<style scoped lang="scss">
.confirm-step {
  display: flex;
  flex-direction: column;
  gap: var(--sp-ref-scale-16);
  // Fill the modal body so the preview table gets a real height limit instead
  // of making the whole modal grow.
  height: 100%;
  min-height: 0;

  &__summary {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    margin: 0;
  }

  &__warning {
    // These are file-level errors: use the error token (the warning token's
    // yellow fails contrast on the light surface).
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    flex: 0 1 auto;
    max-height: 12rem;
    min-height: var(--sp-ref-scale-48);
    margin: 0;
    overflow-y: auto;
  }

  &__error {
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    min-height: 1.25rem;
  }
}
</style>
